<!-- This component is used to display the footer, which conditionally contains the 'Reviewed & Complete' and 'Remove' buttons. -->
<template>
	<div
		v-show="sessionDocumentsStore.pageState.status !== 'complete'"
		class="sticky bottom-0 left-0 z-20 flex w-full items-center bg-white px-8 py-6"
		:class="
			currentStepIsNotDynamic ||
			currentPaymentStepIsHtml ||
			currentOapStepIsHtml ||
			currentEverplansStepIsHtml
				? 'justify-end'
				: 'justify-between'
		"
	>
		<button
			v-if="currentPaymentStepIsDocument"
			data-test="change-payment-type-payment-step"
			type="button"
			class="text-secondary-400! flex cursor-pointer items-center gap-2 font-sans font-light"
			@click="sessionDocumentsStore.setPaymentStepTo('html')"
		>
			<ArrowLongLeftIcon class="h-6 w-6" />
			<span>{{ t('document.changePaymentDetails') }}</span>
		</button>

		<button
			v-if="currentOapStepIsDocument"
			class="text-secondary-400! flex cursor-pointer items-center gap-2 font-sans font-light"
			@click="sessionDocumentsStore.setOapStepTo('html')"
		>
			<ArrowLongLeftIcon class="h-6 w-6" />
			<span>{{ t('document.changePaymentDetails') }}</span>
		</button>

		<button
			v-if="currentEverplansStepIsDocument"
			class="text-secondary-400! flex cursor-pointer items-center gap-2 font-sans font-light"
			@click="sessionDocumentsStore.setEverplansStepTo('html')"
		>
			<ArrowLongLeftIcon class="h-6 w-6" />
			<span>{{ t('document.changePaymentDetails') }}</span>
		</button>
		<div>
			<div
				id="paymentNext"
				v-show="currentPaymentStepIsHtml"
			/>
			<div
				id="oapEnrollmentNext"
				v-show="currentOapStepIsHtml"
			/>
			<div
				id="everplansEnrollmentNext"
				v-show="currentEverplansStepIsHtml"
			/>
			<div
				id="documentControls"
				v-show="
					currentStepIsNotDynamic ||
					currentPaymentStepIsDocument ||
					currentOapStepIsDocument ||
					currentEverplansStepIsDocument
				"
			>
				<button
					v-if="!sessionDocumentsStore.currentStep?.completed"
					data-test="review-and-complete-button"
					type="button"
					class="bg-accent-400! text-accent-700! hover:bg-accent-500! hover:text-accent-800! disabled:text-secondary-400! h-12 w-52 rounded-md font-bold disabled:cursor-not-allowed disabled:bg-slate-200!"
					:disabled="
						!sessionDocumentsStore.currentStep?.valid &&
						sessionDocumentsStore.currentStep?.type !== 'document'
					"
					@click="validateForm"
				>
					{{
						sessionDocumentsStore.currentStep?.label === 'confirmation'
							? t('document.confirm')
							: t('document.finalizeDocument')
					}}
				</button>
				<button
					v-if="currentStep?.completed"
					type="button"
					class="h-12 w-52 rounded-md border border-red-500! font-extrabold text-red-500! hover:border-red-600! hover:bg-red-500! hover:text-white!"
					@click="edit"
				>
					{{ t('document.editDocument') }}
				</button>
			</div>
		</div>
		<Teleport
			to="#edit-document-confirm-modal"
			v-if="confirmModalOpen"
		>
			<InlineModal
				v-model:open="confirmModalOpen"
				variant="small"
			>
				<template #header>
					<div class="mb-1 flex w-full items-center justify-end gap-3">
						<button
							type="button"
							@click="confirmModalOpen = false"
						>
							<XMarkIcon class="text-secondary-300 h-6 w-6" />
						</button>
					</div>
				</template>
				<div
					class="flex flex-col items-center justify-center gap-4 text-center"
				>
					<p class="text-secondary-900! font-bold">
						{{ t('document.editDocumentConfirmation') }}
					</p>
					<p class="px-8 pb-4 text-sm font-extralight text-slate-500!">
						{{
							t(
								currentStep?.type === 'document'
									? 'document.editDocumentSignatureConfirmationDescription'
									: 'document.editDocumentConfirmationDescription'
							)
						}}
					</p>
					<button
						data-test="edit-document-confirmation-button"
						type="button"
						class="font-bold text-red-500! underline"
						@click="confirmEditDocument"
					>
						{{ t('document.editDocumentConfirmationEdit') }}
					</button>
					<button
						type="button"
						class="bg-accent-400! text-accent-700! hover:bg-accent-500! hover:text-accent-800! mb-2 w-full rounded-lg py-2 font-bold"
						@click="confirmModalOpen = false"
					>
						{{ t('document.cancel') }}
					</button>
				</div>
			</InlineModal>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import InlineModal from '@/components/common/InlineModal.vue'
import { useFoxitFormFilling } from '@/components/documents/Foxit/composables/FoxitFormFilling'
import { useFoxitManager } from '@/components/documents/Foxit/composables/FoxitManager'
import { DocumentType } from '@/enums/DocumentType'
import { usePresentationLocale } from '@/i18n'
import { usePlanStore } from '@/stores/plan'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { ArrowLongLeftIcon, XMarkIcon } from '@heroicons/vue/24/solid'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const { t } = usePresentationLocale()
const { validateForm } = useFoxitFormFilling()
const sessionDocumentsStore = useSessionDocumentsStore()
const planStore = usePlanStore()
const { currentPlanHolderID, currentStep } = storeToRefs(sessionDocumentsStore)
const { editDocument, hasSignedAnnotations, confirmModalOpen } =
	useFoxitManager()

const currentStepIsNotDynamic = computed(
	() =>
		currentStep.value?.label !== 'Payment Authorization' &&
		currentStep.value?.label !== DocumentType.OAPEnrollment &&
		currentStep.value?.label !== DocumentType.EverplansEnrollment
)

const currentPaymentStepIsHtml = computed(() => {
	return (
		currentStep.value?.label === 'Payment Authorization' &&
		planStore.getPlan(currentPlanHolderID.value)?.paymentStep === 'html'
	)
})

const currentOapStepIsHtml = computed(() => {
	return (
		currentStep.value?.label === DocumentType.OAPEnrollment &&
		planStore.getPlan(currentPlanHolderID.value)?.oapStep === 'html'
	)
})

const currentEverplansStepIsHtml = computed(() => {
	return (
		currentStep.value?.label === DocumentType.EverplansEnrollment &&
		planStore.getPlan(currentPlanHolderID.value)?.everplansStep === 'html'
	)
})

const currentPaymentStepIsDocument = computed(() => {
	return (
		currentStep.value?.label === DocumentType.PaymentAuthorization &&
		planStore.getPlan(currentPlanHolderID.value)?.paymentStep === 'document'
	)
})

const currentOapStepIsDocument = computed(() => {
	return (
		currentStep.value?.label === DocumentType.OAPEnrollment &&
		planStore.getPlan(currentPlanHolderID.value)?.oapStep === 'document'
	)
})

const currentEverplansStepIsDocument = computed(() => {
	return (
		currentStep.value?.label === DocumentType.EverplansEnrollment &&
		planStore.getPlan(currentPlanHolderID.value)?.everplansStep === 'document'
	)
})

const edit = async () => {
	if (
		currentStep.value?.label !== 'confirmation' &&
		!(await hasSignedAnnotations())
	) {
		sessionDocumentsStore.handleEditDocument()
		await editDocument()
	} else {
		confirmModalOpen.value = true
	}
}

const confirmEditDocument = async () => {
	confirmModalOpen.value = false
	sessionDocumentsStore.handleEditDocument()
	await editDocument()
}

defineExpose({
	confirmModalOpen,
})
</script>
